<template>
	<div class="server-status">
		<div class="toolbar">
			<div><h2>服务器状态</h2><span class="muted">当前 API 服务所在服务器 · 采样时间：{{ sampledTime }}</span></div>
			<div class="actions">
				<el-switch v-model="autoRefresh" active-text="每 10 秒自动刷新" @change="schedule" />
				<el-button type="primary" size="small" icon="el-icon-refresh" :loading="loading" @click="refresh">刷新</el-button>
			</div>
		</div>
		<el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
		<el-alert title="CPU 和内存按操作系统可见范围采集；容器部署时可能包含宿主机资源。磁盘按卷或挂载点展示，不累加重复挂载。" type="info" :closable="false" show-icon />
		<template v-if="snapshot">
			<el-alert v-for="warning in snapshot.warnings" :key="warning" :title="warning" type="warning" :closable="false" show-icon />
			<div class="metrics">
				<el-card shadow="never">
					<div slot="header">CPU 利用率</div>
					<div class="value">{{ percent(snapshot.cpuPercent) }}</div>
					<el-progress v-if="snapshot.cpuPercent != null" :percentage="snapshot.cpuPercent" :color="color(snapshot.cpuPercent)" />
					<dl v-if="snapshot.cpu" class="cpu-details">
						<div><dt>用户态</dt><dd>{{ percent(snapshot.cpu.userPercent) }}</dd></div>
						<div><dt>内核态</dt><dd>{{ percent(snapshot.cpu.systemPercent) }}</dd></div>
						<div><dt>空闲</dt><dd>{{ percent(snapshot.cpu.idlePercent) }}</dd></div>
						<template v-if="snapshot.cpu.hasLinuxDetails">
							<div><dt>I/O 等待</dt><dd>{{ percent(snapshot.cpu.ioWaitPercent) }}</dd></div>
							<div><dt>虚拟化争用（steal）</dt><dd>{{ percent(snapshot.cpu.stealPercent) }}</dd></div>
						</template>
					</dl>
					<p class="muted">服务可用逻辑处理器：{{ snapshot.processorCount }}（不等于物理核心数）</p>
					<template v-if="snapshot.cpu">
						<p class="muted">实际采样窗口：{{ snapshot.cpu.sampleWindowMs }} 毫秒 · 服务端缓存 5 秒</p>
						<p class="muted">采集范围：{{ snapshot.cpu.scope }}<br>数据来源：{{ snapshot.cpu.source }}</p>
						<p class="muted">利用率是采样窗口内的平均值，不是当前 API 进程占用率；与任务管理器等工具的刷新时段不同，数值可能不同。分项独立四舍五入，相加可能有微小偏差。</p>
						<p v-if="snapshot.cpu.hasLinuxDetails" class="muted">总利用率 = 用户态 + 内核态 + steal；不含空闲和 I/O 等待。内核态包含中断，guest 已计入用户态。</p>
					</template>
				</el-card>
				<el-card shadow="never">
					<div slot="header">物理内存利用率</div>
					<div class="value">{{ percent(snapshot.memory && snapshot.memory.usedPercent) }}</div>
					<template v-if="snapshot.memory">
						<el-progress :percentage="snapshot.memory.usedPercent" :color="color(snapshot.memory.usedPercent)" />
						<p class="muted">总量 {{ bytes(snapshot.memory.totalBytes) }} · 已用 {{ bytes(snapshot.memory.usedBytes) }} · 可用 {{ bytes(snapshot.memory.freeBytes) }}</p>
					</template>
				</el-card>
			</div>
			<el-card shadow="never">
				<div slot="header">系统与版本</div>
				<dl class="details">
					<div v-for="item in details" :key="item.label"><dt>{{ item.label }}</dt><dd>{{ item.value || '暂不可用' }}</dd></div>
				</dl>
			</el-card>
			<el-card shadow="never">
				<div slot="header">磁盘空间 <span class="muted">（未用空间含系统保留；当前账户可用空间可能更小）</span></div>
				<el-table :data="snapshot.disks" empty-text="暂无可读取的磁盘信息" style="width: 100%">
					<el-table-column prop="name" label="磁盘 / 挂载点" min-width="150" show-overflow-tooltip />
					<el-table-column prop="format" label="文件系统" min-width="100" />
					<el-table-column label="总大小" min-width="110"><template slot-scope="s">{{ bytes(s.row.space.totalBytes) }}</template></el-table-column>
					<el-table-column label="已用" min-width="110"><template slot-scope="s">{{ bytes(s.row.space.usedBytes) }}</template></el-table-column>
					<el-table-column label="未用" min-width="110"><template slot-scope="s">{{ bytes(s.row.space.freeBytes) }}</template></el-table-column>
					<el-table-column label="当前账户可用" min-width="120"><template slot-scope="s">{{ bytes(s.row.availableBytes) }}</template></el-table-column>
					<el-table-column label="利用率" min-width="180"><template slot-scope="s"><el-progress :percentage="s.row.space.usedPercent" :color="color(s.row.space.usedPercent)" /></template></el-table-column>
				</el-table>
			</el-card>
		</template>
		<div v-else-if="loading" class="empty">正在采集服务器状态…</div>
	</div>
</template>

<script>
import { GetServerStatus } from "@/api/systemApi";

export default {
	name: "ServerStatus",
	data() {
		return { snapshot: null, loading: false, error: "", autoRefresh: true, timer: null, active: false };
	},
	computed: {
		sampledTime() { return this.snapshot ? new Date(this.snapshot.sampledAt).toLocaleString() : "尚未采集"; },
		details() {
			const s = this.snapshot;
			return [
				{ label: "服务器名称", value: s.hostName },
				{ label: "操作系统及版本", value: s.operatingSystem },
				{ label: "系统架构", value: s.architecture },
				{ label: ".NET 运行时", value: s.runtime },
				{ label: "API 程序集版本", value: s.apiVersion },
				{ label: "Web 版本", value: process.env.VUE_APP_VERSION },
				{ label: "服务已运行", value: this.duration(s.serviceUptimeSeconds) },
				{ label: "服务进程内存", value: this.bytes(s.processMemoryBytes) }
			];
		}
	},
	mounted() {
		this.activate();
		document.addEventListener("visibilitychange", this.visibilityChanged);
	},
	activated() { this.activate(); },
	deactivated() { this.active = false; this.clearTimer(); },
	beforeDestroy() {
		this.active = false;
		this.clearTimer();
		document.removeEventListener("visibilitychange", this.visibilityChanged);
	},
	methods: {
		activate() { if (!this.active) { this.active = true; this.refresh(); } },
		clearTimer() { clearTimeout(this.timer); this.timer = null; },
		visibilityChanged() { if (document.hidden) this.clearTimer(); else if (this.active && this.autoRefresh) this.refresh(); },
		schedule() {
			this.clearTimer();
			if (this.active && this.autoRefresh && !document.hidden) this.timer = setTimeout(this.refresh, 10000);
		},
		async refresh() {
			if (this.loading) return;
			this.clearTimer();
			this.loading = true;
			try {
				const result = await GetServerStatus();
				if (result.code !== 1) {
					this.snapshot = null;
					this.autoRefresh = false;
					throw new Error(result.msg || "无法获取服务器状态");
				}
				this.snapshot = result.data;
				this.error = "";
			} catch (e) {
				this.error = (e.message || "获取服务器状态失败") + (this.snapshot ? "；以下为上次采集数据，请检查采样时间。" : "");
			} finally { this.loading = false; this.schedule(); }
		},
		percent(value) { return value == null ? "暂不可用" : value.toFixed(1) + "%"; },
		color(value) { return value >= 90 ? "#f56c6c" : value >= 75 ? "#e6a23c" : "#409eff"; },
		bytes(value) {
			if (value == null) return "暂不可用";
			const units = ["B", "KiB", "MiB", "GiB", "TiB"];
			let n = value, i = 0;
			while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
			return n.toFixed(i ? 2 : 0) + " " + units[i];
		},
		duration(seconds) {
			const minutes = Math.floor(seconds / 60);
			return `${Math.floor(minutes / 1440)} 天 ${Math.floor(minutes / 60) % 24} 小时 ${minutes % 60} 分钟`;
		}
	}
};
</script>

<style scoped>
.server-status {
	padding: 20px;
	background: #f5f7fa;
	height: 100%;
	min-height: 0;
	flex: 1;
	overflow-y: auto;
	overflow-x: hidden;
	scrollbar-gutter: stable;
	box-sizing: border-box;
}
.toolbar, .actions { display: flex; align-items: center; gap: 16px; justify-content: space-between; flex-wrap: wrap; }
h2 { margin: 0 0 8px; font-size: 22px; color: #303133; }
.muted { color: #909399; font-size: 13px; line-height: 1.8; }
.toolbar, .el-alert, .el-card { margin-bottom: 16px; }
.metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.value { font-size: 32px; font-weight: 600; margin-bottom: 18px; color: #303133; }
.details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; margin: 0; }
.details > div { display: flex; gap: 16px; font-size: 14px; }
.cpu-details { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin: 18px 0; }
.cpu-details > div { display: flex; justify-content: space-between; gap: 8px; font-size: 14px; }
.cpu-details dt { min-width: 0; }
dt { color: #909399; min-width: 115px; }
dd { margin: 0; color: #303133; overflow-wrap: anywhere; }
.empty { padding: 60px; text-align: center; color: #909399; }
@media (max-width: 900px) { .metrics, .details { grid-template-columns: 1fr; } }
</style>
